import React, { useState, useRef } from 'react';
import { Printer, Scale } from 'lucide-react';
import { toast } from 'sonner';
import fetchWithAuth from '../../services/fetchWithAuth';

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('access_token');

const fmtWt = (kg) => {
  if (!kg || kg === 0) return '—';
  if (kg >= 1) return `${kg.toFixed(3)} kg`;
  return `${(kg * 1000).toFixed(1)} g`;
};

const s = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 },
  h1: { fontSize: 16, fontWeight: 700, margin: 0, color: '#222' },
  subtitle: { fontSize: 11, color: '#666', marginTop: 4 },
  meta: { textAlign: 'right', fontSize: 11, color: '#666', lineHeight: 1.7 },
  bold: { fontWeight: 700 },
  sep: { borderTop: '1px solid #ccc', marginTop: 10, marginBottom: 14 },
  row: { display: 'flex', gap: 10, marginBottom: 14 },
  card: { flex: 1, border: '1px solid #ddd', padding: '7px 10px' },
  cardLabel: { fontSize: 8, textTransform: 'uppercase', letterSpacing: 0.5, color: '#666', marginBottom: 1 },
  cardValue: { fontSize: 14, fontWeight: 700, color: '#222' },
  cardPct: { fontSize: 9, color: '#888', marginTop: 1 },
  budgetBar: { height: 12, background: '#eee', borderRadius: 2, overflow: 'hidden', position: 'relative', marginTop: 4 },
  budgetFill: { height: '100%', borderRadius: 2, transition: 'width 0.3s' },
  budgetMark: { position: 'absolute', top: 0, height: '100%', width: 1, background: '#f59e0b' },
  driverBox: { background: '#eef6ff', border: '1px solid #3b82f6', padding: '8px 10px', marginBottom: 14 },
  driverTitle: { fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: '#1d4ed8', marginBottom: 4 },
  driverLine: { display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#222', padding: '1px 0' },
  driverRank: { color: '#1d4ed8', fontWeight: 700, marginRight: 6 },
  sectionHdr: { fontSize: 13, fontWeight: 700, color: '#222', marginTop: 16, marginBottom: 2, borderBottom: '2px solid #222', paddingBottom: 4, display: 'flex', justifyContent: 'space-between' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 10, marginTop: 4 },
  th: { background: '#f5f5f5', textAlign: 'left', padding: '3px 6px', border: '1px solid #ddd', fontWeight: 700, fontSize: 9, textTransform: 'uppercase' },
  thR: { background: '#f5f5f5', textAlign: 'right', padding: '3px 6px', border: '1px solid #ddd', fontWeight: 700, fontSize: 9, textTransform: 'uppercase' },
  td: { padding: '3px 6px', border: '1px solid #ddd', color: '#222' },
  tdR: { padding: '3px 6px', border: '1px solid #ddd', textAlign: 'right', color: '#222' },
  tdBold: { padding: '3px 6px', border: '1px solid #ddd', fontWeight: 700, color: '#222' },
  totalTd: { fontWeight: 700, background: '#e8e8e8', padding: '4px 6px', border: '1px solid #bbb', color: '#222' },
  totalTdR: { fontWeight: 700, background: '#e8e8e8', padding: '4px 6px', border: '1px solid #bbb', textAlign: 'right', color: '#222' },
  footer: { marginTop: 20, borderTop: '1px solid #ccc', paddingTop: 10, display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#666' },
  barInline: { display: 'inline-block', height: 8, background: '#3b82f6', borderRadius: 1, verticalAlign: 'middle', marginRight: 3 },
};

const WtBar = ({ pct }) => (
  <span>{pct}%</span>
);

const PrintOverlay = ({ data, onClose }) => {
  const printRef = useRef(null);
  const today = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

  const handlePrint = () => {
    const content = printRef.current;
    if (!content) return;
    const w = window.open('', '_blank', 'width=960,height=700');
    w.document.write(`<!DOCTYPE html><html><head><title>Weight Analysis</title>
<style>* { margin: 0; padding: 0; box-sizing: border-box; } body { font-family: monospace; color: #222; padding: 24px; font-size: 12px; } @media print { body { padding: 12px; } }</style>
</head><body>${content.innerHTML}</body></html>`);
    w.document.close();
    setTimeout(() => { w.print(); }, 300);
  };

  const budgetColor = data.budget_status === 'over' ? '#ef4444' : data.budget_status === 'warning' ? '#f59e0b' : '#22c55e';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-lg w-[940px] max-h-[90vh] overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-2.5 bg-slate-100 border-b">
          <span className="text-sm font-semibold text-slate-800">Weight Budget Analysis — Print Preview</span>
          <div className="flex gap-2">
            <button onClick={handlePrint} data-testid="weight-analysis-print-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-900 text-white rounded text-xs font-medium hover:bg-slate-700 transition-colors">
              <Printer className="w-3.5 h-3.5" /> Print
            </button>
            <button onClick={onClose}
              className="px-3 py-1.5 bg-slate-200 text-slate-700 rounded text-xs font-medium hover:bg-slate-300 transition-colors">
              Close
            </button>
          </div>
        </div>
        <div className="overflow-y-auto max-h-[calc(90vh-48px)] p-5 bg-white">
          <div ref={printRef} style={{ fontFamily: 'monospace', fontSize: 12, color: '#222' }}>
            {/* Header */}
            <div style={s.header}>
              <div>
                <h1 style={s.h1}>WEIGHT BUDGET ANALYSIS</h1>
                <div style={s.subtitle}>{data.bom_number} {data.bom_name}</div>
              </div>
              <div style={s.meta}>
                <div>Revision: <span style={s.bold}>{data.revision_label}</span></div>
                <div>Date: {today}</div>
              </div>
            </div>
            <div style={s.sep} />

            {/* Budget Status Cards */}
            <div style={s.row}>
              <div style={s.card}>
                <div style={s.cardLabel}>Current Weight</div>
                <div style={s.cardValue}>{data.total_weight_kg.toFixed(3)} kg</div>
              </div>
              <div style={s.card}>
                <div style={s.cardLabel}>Weight Budget</div>
                <div style={s.cardValue}>{data.weight_budget_kg > 0 ? `${data.weight_budget_kg.toFixed(3)} kg` : 'Not Set'}</div>
              </div>
              <div style={s.card}>
                <div style={s.cardLabel}>Margin Remaining</div>
                <div style={{ ...s.cardValue, color: budgetColor }}>{data.weight_budget_kg > 0 ? `${data.margin_kg.toFixed(3)} kg` : '—'}</div>
              </div>
              <div style={{ ...s.card, borderColor: budgetColor }}>
                <div style={s.cardLabel}>Budget Used</div>
                <div style={{ ...s.cardValue, color: budgetColor }}>{data.weight_budget_kg > 0 ? `${data.budget_pct}%` : '—'}</div>
                {data.weight_budget_kg > 0 && (
                  <div style={s.budgetBar}>
                    <div style={{ ...s.budgetFill, width: `${Math.min(data.budget_pct, 100)}%`, background: budgetColor }} />
                    <div style={{ ...s.budgetMark, left: '80%' }} />
                  </div>
                )}
                <div style={{ fontSize: 8, color: '#888', marginTop: 2, textTransform: 'uppercase' }}>{data.budget_status}</div>
              </div>
            </div>

            {/* Top Weight Contributors */}
            <div style={s.driverBox}>
              <div style={s.driverTitle}>Top Weight Contributors</div>
              {data.top_contributors.map((p, i) => (
                <div key={i} style={s.driverLine}>
                  <span><span style={s.driverRank}>#{i + 1}</span> <span style={s.bold}>{p.part_number}</span> {p.description.slice(0, 35)}</span>
                  <span><span style={s.bold}>{fmtWt(p.weight_kg)}</span> <span style={{ color: '#888' }}>{p.pct_of_total}%</span></span>
                </div>
              ))}
            </div>

            {/* Section 1: By Sub-Assembly */}
            <div style={s.sectionHdr}>
              <span>SECTION 1: WEIGHT BY SUB-ASSEMBLY</span>
            </div>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={{ ...s.th, width: 55 }}>Assy #</th>
                  <th style={s.th}>Assembly Name</th>
                  <th style={{ ...s.thR, width: 80 }}>Weight</th>
                  <th style={{ ...s.thR, width: 70 }}>% of Total</th>
                  <th style={{ ...s.th, width: 180 }}>Distribution</th>
                </tr>
              </thead>
              <tbody>
                {data.assembly_breakdown.map((a, i) => (
                  <tr key={i}>
                    <td style={s.tdBold}>{a.part_number}</td>
                    <td style={s.td}>{a.assembly}</td>
                    <td style={s.tdR}>{fmtWt(a.weight_kg)}</td>
                    <td style={s.tdR}><WtBar pct={a.pct_of_total} /></td>
                    <td style={s.td}>
                      <div style={{ background: '#eee', height: 10, borderRadius: 2, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${Math.min(a.pct_of_total, 100)}%`, background: '#3b82f6', borderRadius: 2 }} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Section 2: Material Composition */}
            <div style={s.sectionHdr}>
              <span>SECTION 2: MATERIAL COMPOSITION</span>
            </div>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Material Grade</th>
                  <th style={{ ...s.thR, width: 80 }}>Weight</th>
                  <th style={{ ...s.thR, width: 70 }}>% of Total</th>
                  <th style={{ ...s.thR, width: 80 }}>Density (kg/m³)</th>
                  <th style={s.th}>Parts Using</th>
                  <th style={{ ...s.th, width: 180 }}>Distribution</th>
                </tr>
              </thead>
              <tbody>
                {data.material_composition.map((m, i) => (
                  <tr key={i}>
                    <td style={s.tdBold}>{m.grade}</td>
                    <td style={s.tdR}>{fmtWt(m.weight_kg)}</td>
                    <td style={s.tdR}><WtBar pct={m.pct_of_total} color="#6366f1" /></td>
                    <td style={s.tdR}>{m.density > 0 ? m.density.toLocaleString() : '—'}</td>
                    <td style={{ ...s.td, fontSize: 9 }}>{m.parts.join(', ')}</td>
                    <td style={s.td}>
                      <div style={{ background: '#eee', height: 10, borderRadius: 2, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${Math.min(m.pct_of_total, 100)}%`, background: '#6366f1', borderRadius: 2 }} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Section 3: Weight by Category */}
            <div style={s.sectionHdr}>
              <span>SECTION 3: WEIGHT BY CATEGORY</span>
            </div>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Category</th>
                  <th style={{ ...s.thR, width: 80 }}>Weight</th>
                  <th style={{ ...s.thR, width: 70 }}>% of Total</th>
                  <th style={{ ...s.th, width: 250 }}>Distribution</th>
                </tr>
              </thead>
              <tbody>
                {data.category_breakdown.map((c, i) => (
                  <tr key={i}>
                    <td style={s.tdBold}>{c.category}</td>
                    <td style={s.tdR}>{fmtWt(c.weight_kg)}</td>
                    <td style={s.tdR}><WtBar pct={c.pct_of_total} color="#10b981" /></td>
                    <td style={s.td}>
                      <div style={{ background: '#eee', height: 10, borderRadius: 2, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${Math.min(c.pct_of_total, 100)}%`, background: '#10b981', borderRadius: 2 }} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Section 4: Optimization Targets */}
            <div style={s.sectionHdr}>
              <span>SECTION 4: WEIGHT CONTRIBUTORS</span>
              <span style={{ fontSize: 9, color: '#666', fontWeight: 400 }}>sorted heaviest to lightest</span>
            </div>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={{ ...s.th, width: 75 }}>Part #</th>
                  <th style={s.th}>Description</th>
                  <th style={{ ...s.thR, width: 75 }}>Weight</th>
                  <th style={{ ...s.thR, width: 50 }}>% Total</th>
                  <th style={{ ...s.th, width: 65 }}>Material</th>
                  <th style={{ ...s.thR, width: 80 }}>Density (kg/m³)</th>
                  <th style={{ ...s.th, width: 55 }}>Form</th>
                </tr>
              </thead>
              <tbody>
                {data.optimization_targets.map((p, i) => (
                  <tr key={i} style={i === 0 ? { background: '#fff8e1' } : {}}>
                    <td style={s.tdBold}>{p.part_number}</td>
                    <td style={s.td}>{p.description.slice(0, 35)}</td>
                    <td style={s.tdR}>{fmtWt(p.weight_kg)}</td>
                    <td style={s.tdR}>{p.pct_of_total}%</td>
                    <td style={s.td}>{p.material_grade || '—'}</td>
                    <td style={s.tdR}>{p.material_density > 0 ? p.material_density.toLocaleString() : '—'}</td>
                    <td style={s.td}>{p.material_form || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Grand Total */}
            <table style={{ ...s.table, marginTop: 14 }}>
              <tbody>
                <tr>
                  <td style={{ ...s.totalTd, fontSize: 12, borderTop: '2px solid #222', textAlign: 'right', paddingRight: 12 }} colSpan={4}>TOTAL PRODUCT WEIGHT</td>
                  <td style={{ ...s.totalTdR, fontSize: 12, borderTop: '2px solid #222', width: 120 }}>{data.total_weight_kg.toFixed(3)} kg</td>
                </tr>
              </tbody>
            </table>

            {/* Footer */}
            <div style={s.footer}>
              <span>Generated from HADEN ATLAS PLM</span>
              <span>Printed: {today}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const WeightAnalysisButton = ({ bomId, revisionId }) => {
  const [showPreview, setShowPreview] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    setLoading(true);
    try {
      const res = await fetchWithAuth(
        `${API}/api/v1/bom/${bomId}/revisions/${revisionId}/weight-analysis`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      if (!res.ok) throw new Error('Failed');
      const d = await res.json();
      setData(d);
      setShowPreview(true);
    } catch {
      toast.error('Failed to generate Weight Analysis');
    }
    setLoading(false);
  };

  return (
    <>
      <button onClick={handleClick} disabled={loading} data-testid="weight-analysis-btn"
        className="flex items-center gap-1 px-2 py-0.5 text-[10px] rounded transition-colors bg-slate-700/50 hover:bg-slate-600/50 text-slate-400 hover:text-white disabled:opacity-50">
        <Scale className="w-3 h-3" />{loading ? 'Loading...' : 'Weight Analysis'}
      </button>
      {showPreview && data && <PrintOverlay data={data} onClose={() => setShowPreview(false)} />}
    </>
  );
};
